import React, { useMemo } from "react";

import { Box, IconButtonProps, styled, IconButton } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { colors } from "@zapalacorp/tokens";
import { AppBadge } from "Components/AppBadge";
import { Cronjob } from "Models/cronjob";
import { useTranslation } from "react-i18next";
import { formatSimpleDate, formatSimpleTime } from "Util/formatDateAndTime";
import { useQuery } from "@tanstack/react-query";
import { BranchKeys, CompanyKeys } from "Util/queries";
import CompanyService from "Services/company.service";
import BranchService from "Services/branch.service";
import { format, utcToZonedTime } from "date-fns-tz";
import { AppBadgeStyle, InfoBox, InfoDescription } from "./styles";
import InfoIcon from "Components/Generic/Icons/Info";

export const ChipCloseButton = styled((props: IconButtonProps) => (
  <IconButton size="small" {...props}>
    <CloseIcon fontSize="small" />
  </IconButton>
))(() => ({
  borderRadius: "8px !important",
  marginLeft: "auto",
  padding: "0px !important",
}));

interface SelectedFiltersSectionProps {
  cronJob: Cronjob;
  updateCronJob: (cronJob?: Cronjob) => void;
  setChanges: (changes: string) => void;
}

export const SelectedFiltersSection = (props: SelectedFiltersSectionProps) => {
  const { cronJob, updateCronJob, setChanges } = props;
  const { t: translate } = useTranslation();

  const filtersLabel = {
    companyId: translate("company"),
    period: translate("period"),
    status: translate("status"),
    branchId: translate("branches"),
    type: translate("type"),
    startDate: translate("dateRange"),
  };

  const { data: companyList } = useQuery({
    queryKey: CompanyKeys.getAll([]),
    queryFn: async () =>
      await CompanyService.getAll({
        id: JSON.parse(JSON.parse(cronJob.filters)?.companyId),
        attributes: JSON.stringify(["id", "name"]),
      }).then(({ data }) => data),
    enabled: !!cronJob.filters.includes("companyId"),
    initialData() {
      return [];
    },
  });

  const { data: branchList } = useQuery({
    queryKey: BranchKeys.getAll([]),
    queryFn: async () =>
      await BranchService.getAll({
        id: JSON.parse(JSON.parse(cronJob.filters)?.branchId),
      }).then(({ data }) => data),
    enabled: !!cronJob.filters.includes("branchId"),
    initialData() {
      return [];
    },
  });

  const filtersData = useMemo(() => {
    const filters = JSON.parse(cronJob?.filters);
    const filtersKey = Object.keys(JSON.parse(cronJob?.filters));
    const data: any[] = [];
    filtersKey.forEach((key) => {
      if (key !== "endDate") {
        let filtersValue = filters[key];
        const label = filtersLabel[key as keyof typeof filtersLabel];

        if (key === "startDate") {
          filtersValue = `${format(
            utcToZonedTime(filters[key], Intl.DateTimeFormat().resolvedOptions().timeZone),
            "dd/MM/yyyy"
          )} -
          ${format(
            utcToZonedTime(filters["endDate"], Intl.DateTimeFormat().resolvedOptions().timeZone),
            "dd/MM/yyyy"
          )}`;
        }

        if (key === "companyId") {
          filtersValue = companyList.map((company) => company.name)?.join(", ");
        }

        if (key === "branchId") {
          filtersValue = branchList.map((branch) => branch.name)?.join(", ");
        }

        data.push({
          key,
          label,
          value: filtersValue,
        });
      }
    });

    return data;
  }, [cronJob, companyList, branchList]);

  const removeFilters = (filterKey: string) => {
    const filters = JSON.parse(cronJob.filters);
    delete filters[filterKey];

    if (filterKey === "startDate") {
      delete filters["endDate"];
    }

    updateCronJob({ ...cronJob, filters: JSON.stringify(filters) });
  };

  return (
    <div>
      <Box display="flex" gap="8px" flexWrap="wrap">
        {filtersData.map((filter) => {
          return (
            <AppBadgeStyle
              key={filter.value}
              color={colors.brandBlue500}
              backgroundColor={colors.brandBlue50}
            >
              <span>
                <b>{filter.label}:</b>
                &nbsp;
                <span>{filter.value}</span>
              </span>

              {filter.key !== "companyId" && (
                <ChipCloseButton
                  color="inherit"
                  onClick={() => {
                    removeFilters(filter.key);
                    setChanges("filters");
                  }}
                />
              )}
            </AppBadgeStyle>
          );
        })}
      </Box>
      <InfoBox
        display="flex"
        sx={{
          gap: "12px",
        }}
        alignItems="flex-start"
      >
        <InfoIcon height={20} width={20} color={colors.gray700} />
        <InfoDescription>
          {translate("onceYouClickOn")} <b>"{translate("schedule")}"</b>{" "}
          {translate("youWillReceiveThisReportWithAllTheSelectedFiltersAndSetFrequency")}{" "}
          {translate("emailDeliveryMayBeDelayedDueFactorsBeyondOurControl")}{" "}
          {translate("pleaseWhitelistToAvoidEmailsGoingToSpam")}
        </InfoDescription>
      </InfoBox>
    </div>
  );
};
